import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from '../Modal';
import {
  CreateExpertResult,
  domainExpertApi,
  getExpertErrorMessage,
  isRequestCancelled,
} from '../../lib/domain-expert-api';
import { btnPrimary, btnSecondary, helpClass, inputClass, labelClass } from './ui';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface AddExpertModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (result: CreateExpertResult) => void;
}

export default function AddExpertModal({ isOpen, onClose, onCreated }: AddExpertModalProps) {
  const queryClient = useQueryClient();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFullName('');
    setEmail('');
    setError(null);
  };

  const mutation = useMutation({
    mutationFn: domainExpertApi.createExpert,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['expert', 'experts'] });
      reset();
      onCreated(result);
    },
    onError: (err) => {
      if (isRequestCancelled(err)) return;
      setError(getExpertErrorMessage(err, "We couldn't add this user. Please try again."));
    },
  });

  const handleClose = () => {
    if (mutation.isLoading) return;
    reset();
    onClose();
  };

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const name = fullName.trim();
    const address = email.trim();
    if (!name || !address) {
      setError('Enter a name and an email.');
      return;
    }
    if (!EMAIL_PATTERN.test(address)) {
      setError('Enter a valid email.');
      return;
    }
    setError(null);
    mutation.mutate({ full_name: name, email: address });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add user"
      size="sm"
      footer={
        <>
          <button type="button" onClick={handleClose} disabled={mutation.isLoading} className={`${btnSecondary} mt-3 sm:mt-0`}>
            Cancel
          </button>
          <button type="button" onClick={() => handleSubmit()} disabled={mutation.isLoading} className={btnPrimary}>
            {mutation.isLoading ? 'Adding…' : 'Add and send invite'}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="expert-name" className={labelClass}>
            Name
          </label>
          <input
            id="expert-name"
            type="text"
            autoFocus
            value={fullName}
            maxLength={255}
            onChange={(e) => setFullName(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="expert-email" className={labelClass}>
            Email
          </label>
          <input
            id="expert-email"
            type="email"
            value={email}
            maxLength={255}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
          />
          <p className={helpClass}>We'll email them an invite to set up their account.</p>
        </div>
        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
        <button type="submit" className="hidden" />
      </form>
    </Modal>
  );
}
